import { defineStore } from 'pinia';
import { contributorsPreview } from '🔗/utils/audio';
import useUserStore from '🔗/stores/user';

const useAppPluginStore = defineStore('app-plugin', {
    state: () => ({
        activeProfileId: null,
        profiles: [],

        isIframeReady: false,
        frame: null,
        app: null
    }),
    getters: {
        profile: (state) => state.activeProfileId ? state.profiles.find(item =>
                item.id === state.activeProfileId
            ) : null
    },
    actions: {
        setActiveProfile(id) {
            const profile = this.profiles.find(item => item.id === id);

            if (!profile) {
                if (this.profiles?.length) {
                    this.activeProfileId = this.profiles[0].id;
                    localStorage['apps-profile-id'] = this.profiles[0].id;
                }
                return;
            }

            this.activeProfileId = profile.id;
            localStorage['apps-profile-id'] = this.activeProfileId;


            this.send({
                type: 'active-profiles-id',
                payload: {
                    'active-profiles-id': this.activeProfileId
                }
            })
        },
        syncProfiles() {
            this.profiles = [];

            const user = useUserStore();
            if (!user.token) {
                return;
            }

            if (user.profile?.id) {
                this.profiles.push({
                    type: 'user',
                    id: user.profile.id,
                    code: user.profile.userName,
                    name: user.profile.firstName || user.profile.userName,
                    img: user.profile.profileImage,
                    links: []
                })
            }

            if (user.artists?.length) {
                this.profiles.splice(this.profiles.length - 1, 0, ...user.artists.map(item => ({
                    type: 'artist',
                    id: item.id,
                    name: item.name,
                    code: item.code,
                    links: item.links || [],
                    img: item.profileImage
                })))
            }

            this.send({
                type: 'profiles',
                payload: {
                    profiles: this.profiles
                }
            })

            if (!this.profiles?.length) {
                return;
            }

            this.setActiveProfile(localStorage['apps-profile-id']);
        },

        setApp(item) {
            if (!item?.code) {
                return;
            }

            this.isIframeReady = false;

            this.app = item;
        },
        clearApp() {
            delete this.app;
        },
        setFrame(frame) {
            this.frame = frame;
        },
        getImage(item) {
            return {
                crop: import.meta.env.VITE_APP_FILES_URL + item?.crop,
                original: import.meta.env.VITE_APP_FILES_URL + item?.original,
                resized: import.meta.env.VITE_APP_FILES_URL + item?.resized
            }
        },
        getArtists(items, role) {
            return items?.filter(item => item.role === role).map(item => ({
                name: item.artist?.name || item.unregisteredArtistName,
                profileImage: item.artist?.profileImage ? this.getImage(item.artist.profileImage) : null
            }))
        },

        beautyTrack(track) {
            if (!track) {
                return;
            }

            return {
                id: track.id,
                bpm: track.bpm,
                cover: this.getImage(track.cover),
                duration: track.duration,
                fromProject: track.fromProject,
                isExplicit: track.isExplicit,
                title: track.title,
                version: track.version,
                wave: track.wave,
                contributors: {
                    primary: this.getArtists(track.contributors, 'PRIMARY'),
                    featured: this.getArtists(track.contributors, 'FEATURED'),
                },
                artists: contributorsPreview(track.contributors)
            }
        },

        beautyRelease(release) {
            if (!release) {
                return;
            }

            return {
                id: release.id,
                title: release.title,
                cover: this.getImage(release.cover),
                fromProject: release.fromProject,
                contributors: {
                    primary: this.getArtists(release.contributors, 'PRIMARY'),
                    featured: this.getArtists(release.contributors, 'FEATURED'),
                },
                artists: contributorsPreview(release.contributors)
            }
        },

        beautyArtist(artist) {
            if (!artist) {
                return;
            }

            return {
                id: artist.id,
                name: artist.name,
                code: artist.code,
                profileImage: this.getImage(artist.profileImage)
            }
        },

        beautyProfile(profile) {
            if (!profile) {
                return;
            }


            return {
                ...profile,
                active: this.activeProfileId === profile.id,
                img: this.getImage(profile.img)
            }
        },

        beautyPlaylist(playlist) {
            if (!playlist) {
                return;
            }

            return {
                id: playlist.id,
                name: playlist.name,
                code: playlist.code,
                cover: this.getImage(playlist.cover),
                title: stores.locale().title(playlist.title),
                type: playlist.type
            }
        },

        beautyQueue(queue) {
            if (!queue) {
                return;
            }

            return {
                items: queue.items?.map(item => this.beautyTrack(item)),
                release: this.beautyRelease(queue.release),
                artist: this.beautyArtist(queue.artist),
                playlist: this.beautyPlaylist(queue.playlist)
            }
        },

        buildMessage(type, payload) {
            const message = {
                type,
                payload: {...payload || {}}
            }


            if (message.payload.profiles?.length) {
                message.payload.profiles = message.payload.profiles.map(item =>
                    this.beautyProfile(item)
                );
            }
            // if (message.payload?.item) {
            //     if (this.track?.id !== message.payload?.item?.id) {

            //         this.track = this.beautyTrack(message.payload.item);
            //         message.payload.track = this.track;
            //     }
            // }
            // delete message.payload?.item;

            // if (message.type === 'queue' && message.payload) {
            //     message.payload = this.beautyQueue(message.payload);
            // }

            if (!message.payload || !Object.keys(message.payload).length) {
                delete message.payload;
            }

            return message;
        },

        getStyleTokens() {
            const rootStyles = window.getComputedStyle(document.documentElement);
            const styles = {};

            const tokens = [
                '--max-safe-area-inset-top',
                '--tg-safe-area-inset-top',
                '--max-content-safe-area-inset-top',
                '--tg-content-safe-area-inset-top'
            ]

            tokens.forEach(style => {
                styles[style] = rootStyles.getPropertyValue(style).trim();
            });

            return styles;
        },

        sendInitMessage() {
            if (!this.frame?.contentWindow || !this.isIframeReady) {
                return
            }

            const message = {
                type: 'APP_INIT',
                payload: {
                    locale: {
                        locale: stores.locale().locale,
                        rtl: stores.locale().rtl
                    },
                    theme: stores.settings().theme,
                    vibration: stores.settings().vibration,
                    styles: this.getStyleTokens(),
                    profiles: this.profiles.map((item) => this.beautyProfile(item)),
                    activeProfileId: this.activeProfileId
                }
            }

            this.frame.contentWindow.postMessage(JSON.parse(JSON.stringify(message)), '*');
        },

        send({ type, payload }) {
            if (!this.frame?.contentWindow || !this.isIframeReady) {
                return
            }

            const message = this.buildMessage(type, payload);

            if (message) {
                this.frame.contentWindow.postMessage(JSON.parse(JSON.stringify(message)), '*');
            }
        },
        sendBuffer({ type, buffer }) {
            if (!this.frame?.contentWindow || !this.isIframeReady) {
                return
            }

            this.frame.contentWindow.postMessage({
                type,
                payload: { buffer }
            }, '*', [buffer]);
        }
    }
});

export default useAppPluginStore;
